import {
    AMARON_KANBAN_COLUMN_FIELD,
    dispositionSlugMatchesSearch,
    listAmaronDispositionSearchHaystack,
    resolveAmaronKanbanColumnSlugForViewLevel,
    type AmaronDispositionKanbanViewLevel,
} from "~/constants/amaronDispositionMapping";
import {resolveAmaronDispositionQueueAction, resolveAmaronLeadPriority} from "~/constants/amaronDispositionPriority";
import {sortJourneysByAmaronSourcePriorityColumn} from "~/utilities/workspaceSpecific/amaronPriority";
import {
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    amaronJourneySyntheticFunction,
    getAmaronDispositionIdsForLevels,
    normalizeAmaronJourneyContactNumber,
    resolveAmaronJourneyFlatDispositionForKanban,
    resolveAmaronJourneyLatestDispositionActivityMs,
    splitAmaronFlatDispositionIntoLevels,
} from "~/domainModels/workspaceSpecific/amaron";
import type {InfiniteData, QueryClient} from "@tanstack/react-query";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import type {SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {isAmaronJourneyDispositionUiFilterColumn, readAmaronDispositionLevelsFromFilters} from "~/domainModels/workspaceSpecific/amaronDispositionFilters";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {AmaronEventContainerTypes} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import {applyAmaronDispositionTrackerClientFilters, hasAmaronDispositionKanbanEmptyInFilter} from "~/utilities/amaronDispositionTrackerClientFilters";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {getEventContainers, getEventContainersWithEvents, type EventContainerWithEvents} from "~/utilities/apis/api";
import {
    buildAmaronDispositionTrackerQueryRequest,
    queryAmaronDispositionTracker,
    type AmaronDispositionTrackerQueryResult,
} from "~/utilities/apis/amaronDispositionTrackerApi";
import {uniqueUuid} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getEventContainerDispositionColumnId, getOptionalEventContainerDispositionId} from "~/utilities/ottopilotSemanticColumns";

export const AMARON_DISPOSITION_KANBAN_DATASET_QUERY_KEY_ROOT = "amaronDispositionKanbanDataset";
export const AMARON_DISPOSITION_TRACKER_QUERY_KEY_ROOT = "amaronDispositionTrackerQuery";
export const AMARON_DISPOSITION_TRACKER_DATA_VIEWER_QUERY_KEY_ROOT =
    uniqueUuid`b9e4d3f2-5c6a-7b8d-0e1f-2a3b4c5d6e7f`;

export type AmaronDispositionKanbanJourney = EventContainerWithEvents & {
    [AMARON_KANBAN_COLUMN_FIELD]: string;
    amaronQueueRank: number;
};

const dispositionSearchHaystack = listAmaronDispositionSearchHaystack();

function primitiveToStringOrNull(raw: unknown): string | null {
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean" || typeof raw === "bigint") {
        return raw.toString();
    }
    return null;
}

function journeyMatchesDispositionLevelFilters(
    journey: EventContainerWithEvents,
    filterConfiguration: FilterConfiguration,
    eventContainerTemplate: EventContainerTemplate,
): boolean {
    const activeLevels = readAmaronDispositionLevelsFromFilters(filterConfiguration.lineItems, "journey");
    const hasAnyLevelFilter =
        activeLevels.l1Values !== undefined ||
        activeLevels.l2Values !== undefined ||
        activeLevels.l3Values !== undefined ||
        activeLevels.l4Values !== undefined;
    if (hasAnyLevelFilter !== true) {
        return true;
    }
    const matchingIds = getAmaronDispositionIdsForLevels(activeLevels);
    if (matchingIds.length === 0) {
        return false;
    }
    const dispositionId = getOptionalEventContainerDispositionId(journey, eventContainerTemplate);
    return dispositionId !== null && matchingIds.includes(dispositionId);
}

function journeyMatchesLocalSearch(
    journey: AmaronDispositionKanbanJourney,
    searchConfiguration: SearchConfiguration,
    displayNameTemplate: string,
    eventContainerTemplate: EventContainerTemplate,
): boolean {
    const searchText = searchConfiguration.searchValue?.trim() ?? "";
    if (searchText === "") {
        return true;
    }
    const normalized = searchText.toLowerCase();

    const displayName = processOttopilotStringTemplate(displayNameTemplate, journey.additionalColumnValues as Record<Uuid, unknown>);
    if (displayName.toLowerCase().includes(normalized)) {
        return true;
    }

    const contactRaw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
    if (typeof contactRaw === "string" && contactRaw.toLowerCase().includes(normalized)) {
        return true;
    }

    if (String(journey.id).toLowerCase().includes(normalized)) {
        return true;
    }

    const customerNameRaw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID];
    if (typeof customerNameRaw === "string" && customerNameRaw.toLowerCase().includes(normalized)) {
        return true;
    }

    for (const columnId of [
        AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
        AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
        AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
        AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    ]) {
        const levelRaw = journey.additionalColumnValues[columnId];
        if (typeof levelRaw === "string" && dispositionSlugMatchesSearch(levelRaw, searchText)) {
            return true;
        }
    }

    const flat = resolveAmaronJourneyFlatDispositionForKanban(
        journey,
        getOptionalEventContainerDispositionId(journey, eventContainerTemplate),
    );
    if (flat !== "") {
        if (flat.toLowerCase().includes(normalized)) {
            return true;
        }
        const levels = splitAmaronFlatDispositionIntoLevels(flat);
        for (const part of [levels.l1, levels.l2, levels.l3, levels.l4]) {
            if (part !== "" && dispositionSlugMatchesSearch(part, searchText)) {
                return true;
            }
        }
    }

    return dispositionSearchHaystack.some((entry) => entry.includes(normalized));
}

function compareAmaronDispositionKanbanJourneyRecency(left: AmaronDispositionKanbanJourney, right: AmaronDispositionKanbanJourney): number {
    const activityDelta = resolveAmaronJourneyLatestDispositionActivityMs(right) - resolveAmaronJourneyLatestDispositionActivityMs(left);
    if (activityDelta !== 0) {
        return activityDelta;
    }
    const updatedDelta = new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
    if (Number.isFinite(updatedDelta) && updatedDelta !== 0) {
        return updatedDelta;
    }
    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
}

export function toAmaronDispositionKanbanJourney(
    journey: EventContainerWithEvents,
    viewLevel: AmaronDispositionKanbanViewLevel,
    eventContainerTemplate: EventContainerTemplate,
): AmaronDispositionKanbanJourney | null {
    const flat = resolveAmaronJourneyFlatDispositionForKanban(
        journey,
        getOptionalEventContainerDispositionId(journey, eventContainerTemplate),
    );
    const levels =
        flat !== ""
            ? splitAmaronFlatDispositionIntoLevels(flat)
            : {
                  l1: primitiveToStringOrNull(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]) ?? "",
                  l2: primitiveToStringOrNull(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]) ?? "",
                  l3: primitiveToStringOrNull(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]) ?? "",
                  l4: primitiveToStringOrNull(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID]) ?? "",
              };
    const columnSlug = resolveAmaronKanbanColumnSlugForViewLevel(levels, viewLevel);
    if (columnSlug.trim() === "") {
        return null;
    }
    const flatForPriority = flat !== "" ? flat : [levels.l1, levels.l2, levels.l3, levels.l4].filter((part) => part !== "").join("_");
    const priority = resolveAmaronLeadPriority(journey, eventContainerTemplate);
    const queueAction = resolveAmaronDispositionQueueAction(flatForPriority, priority);
    return {
        ...journey,
        [AMARON_KANBAN_COLUMN_FIELD]: columnSlug,
        amaronQueueRank: queueAction.queueRank,
    };
}

/**
 * One card per contact: keep the journey whose disposition was updated most recently and merge events for the funnel ring.
 */
function dedupeAmaronDispositionKanbanJourneysByContact(
    journeys: Array<AmaronDispositionKanbanJourney>,
    viewLevel: AmaronDispositionKanbanViewLevel,
    eventContainerTemplate: EventContainerTemplate,
): Array<AmaronDispositionKanbanJourney> {
    const journeysWithoutContact: Array<AmaronDispositionKanbanJourney> = [];
    const journeysByContact = new Map<string, Array<AmaronDispositionKanbanJourney>>();

    for (const journey of journeys) {
        const contactKey = normalizeAmaronJourneyContactNumber(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]);
        if (contactKey === "") {
            journeysWithoutContact.push(journey);
            continue;
        }
        const bucket = journeysByContact.get(contactKey) ?? [];
        bucket.push(journey);
        journeysByContact.set(contactKey, bucket);
    }

    const deduped: Array<AmaronDispositionKanbanJourney> = [...journeysWithoutContact];
    for (const group of journeysByContact.values()) {
        if (group.length === 1) {
            deduped.push(group[0]!);
            continue;
        }
        const winner = [...group].sort(compareAmaronDispositionKanbanJourneyRecency)[0]!;
        const eventsById = new Map<string, AmaronDispositionKanbanJourney["events"][number]>();
        for (const journey of group) {
            for (const event of journey.events) {
                eventsById.set(String(event.id), event);
            }
        }
        const merged = toAmaronDispositionKanbanJourney({...winner, events: [...eventsById.values()]}, viewLevel, eventContainerTemplate);
        if (merged !== null) {
            deduped.push(merged);
        }
    }
    return deduped;
}

export function generateAmaronDispositionKanbanData(args: {
    journeys: Array<EventContainerWithEvents>;
    filterConfiguration: FilterConfiguration;
    searchConfiguration: SearchConfiguration;
    viewLevel: AmaronDispositionKanbanViewLevel;
    journeysDisplayNameOttopilotStringTemplate: string;
    /** When API search already applied, skip redundant local search pass. */
    apiSearchApplied?: boolean;
    eventContainerTemplate: EventContainerTemplate;
}): Array<AmaronDispositionKanbanJourney> {
    const clientFilteredJourneys = applyAmaronDispositionTrackerClientFilters(
        args.journeys,
        args.filterConfiguration,
        args.eventContainerTemplate,
    );

    const filtered = clientFilteredJourneys.filter((journey) => {
        if (journeyMatchesDispositionLevelFilters(journey, args.filterConfiguration, args.eventContainerTemplate) !== true) {
            return false;
        }
        return true;
    });

    const withKanbanField: Array<AmaronDispositionKanbanJourney> = [];
    for (const journey of filtered) {
        const row = toAmaronDispositionKanbanJourney(journey, args.viewLevel, args.eventContainerTemplate);
        if (row !== null) {
            withKanbanField.push(row);
        }
    }

    const searched =
        args.apiSearchApplied === true
            ? withKanbanField
            : withKanbanField.filter((journey) =>
                  journeyMatchesLocalSearch(
                      journey,
                      args.searchConfiguration,
                      args.journeysDisplayNameOttopilotStringTemplate,
                      args.eventContainerTemplate,
                  ),
              );

    const latestOnlyByContact = dedupeAmaronDispositionKanbanJourneysByContact(searched, args.viewLevel, args.eventContainerTemplate);

    return sortJourneysByAmaronSourcePriorityColumn(latestOnlyByContact);
}

/** Shared react-query key for the full kanban dataset (per-column slug filters are applied in memory). */
function buildAmaronDispositionKanbanDatasetCacheKey(args: {
    workspaceId: Uuid;
    dispositionLevel: AmaronDispositionKanbanViewLevel;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration: SearchConfiguration;
}): Array<unknown> {
    const filterLineItemsForCache = args.filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId !== AMARON_KANBAN_COLUMN_FIELD);
    return [
        AMARON_DISPOSITION_KANBAN_DATASET_QUERY_KEY_ROOT,
        args.workspaceId,
        args.dispositionLevel,
        JSON.stringify(filterLineItemsForCache),
        args.sortConfiguration.revisionId,
        args.searchConfiguration.revisionId,
    ];
}

export function extractKanbanColumnSlugFromFilterConfiguration(filterConfiguration: FilterConfiguration): string | null {
    for (const lineItem of filterConfiguration.lineItems) {
        if (lineItem.columnId !== AMARON_KANBAN_COLUMN_FIELD) {
            continue;
        }
        if (lineItem.operation === FilterOperation.in && lineItem.values.length === 1) {
            return lineItem.values[0] ?? null;
        }
        if (lineItem.operation === FilterOperation.equals && lineItem.value !== null && lineItem.value !== undefined) {
            return primitiveToStringOrNull(lineItem.value);
        }
    }
    return null;
}

/** Strips per-column kanban `in` filters so counts/cards use the shared board filter set. */
export function stripAmaronKanbanColumnFilterLineItems(filterConfiguration: FilterConfiguration): FilterConfiguration {
    return {
        ...filterConfiguration,
        lineItems: filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId !== AMARON_KANBAN_COLUMN_FIELD),
    };
}

export async function fetchAmaronDispositionTrackerQuery(args: {
    workspaceId: Uuid;
    dispositionLevel: AmaronDispositionKanbanViewLevel;
    intellsysBaseUrl: string;
    accessToken: string;
    leadViewFilterLineItems: FilterConfiguration["lineItems"];
    mapFilterConfigurationToFields: (filterConfiguration: FilterConfiguration) => FilterConfiguration;
    mapSortConfigurationToFields: (sortConfiguration: SortConfiguration) => SortConfiguration;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration: SearchConfiguration;
    notificationFilter?: string;
    kanbanColumnSlug?: string | null;
    kanbanColumnSlugForDispositionFilter?: string | null;
    offset: number;
    limit: number;
    facetColumnId?: string;
    includeRows?: boolean;
    includeTotalCount?: boolean;
    includeKanbanColumnCounts?: boolean;
}): Promise<AmaronDispositionTrackerQueryResult> {
    const request = buildAmaronDispositionTrackerQueryRequest({
        workspaceId: args.workspaceId,
        viewLevel: args.dispositionLevel,
        offset: args.offset,
        limit: args.limit,
        filterConfiguration: args.filterConfiguration,
        sortConfiguration: args.sortConfiguration,
        searchConfiguration: args.searchConfiguration,
        leadViewFilterLineItems: args.leadViewFilterLineItems,
        mapFilterConfigurationToFields: args.mapFilterConfigurationToFields,
        mapSortConfigurationToFields: args.mapSortConfigurationToFields,
        notificationFilter: args.notificationFilter,
        kanbanColumnSlug: args.kanbanColumnSlug,
        kanbanColumnSlugForDispositionFilter: args.kanbanColumnSlugForDispositionFilter,
        facetColumnId: args.facetColumnId,
        includeRows: args.includeRows,
        includeTotalCount: args.includeTotalCount,
        includeKanbanColumnCounts: args.includeKanbanColumnCounts,
    });

    const result = await queryAmaronDispositionTracker({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        request: request,
    });
    if (result.success !== true) {
        throw result.error;
    }
    return result.data;
}

export async function fetchAmaronDispositionKanbanDataset(args: {
    queryClient: QueryClient;
    workspaceId: Uuid;
    dispositionLevel: AmaronDispositionKanbanViewLevel;
    intellsysBaseUrl: string;
    accessToken: string;
    fetchLimit: number;
    leadViewFilterLineItems: FilterConfiguration["lineItems"];
    mapFilterConfigurationToFields: (filterConfiguration: FilterConfiguration) => FilterConfiguration;
    mapSortConfigurationToFields: (sortConfiguration: SortConfiguration) => SortConfiguration;
    journeysDisplayNameOttopilotStringTemplate: string;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration: SearchConfiguration;
    journeySyntheticFn: (journey: EventContainerWithEvents) => EventContainerWithEvents;
    eventContainerTemplate: EventContainerTemplate;
}): Promise<Array<AmaronDispositionKanbanJourney>> {
    return args.queryClient.fetchQuery({
        queryKey: buildAmaronDispositionKanbanDatasetCacheKey(args),
        queryFn: async () => {
            if (hasAmaronDispositionKanbanEmptyInFilter(args.filterConfiguration) === true) {
                return [];
            }

            const backendLineItems = [
                ...args.leadViewFilterLineItems,
                ...args.filterConfiguration.lineItems.filter(
                    (lineItem) =>
                        lineItem.columnId !== AMARON_KANBAN_COLUMN_FIELD &&
                        isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) !== true &&
                        args.leadViewFilterLineItems.some(
                            (leadLineItem) => leadLineItem.columnId === lineItem.columnId && leadLineItem.operation === lineItem.operation,
                        ) !== true,
                ),
            ];
            const apiFilterConfiguration = args.mapFilterConfigurationToFields({
                revisionId: generateUuid(),
                lineItems: backendLineItems,
            });
            const apiSortConfiguration = args.mapSortConfigurationToFields(args.sortConfiguration);
            const fetchArgs = {
                workspaceId: args.workspaceId,
                templateId: AmaronEventContainerTypes.Journeys as Uuid,
                offset: 0,
                limit: args.fetchLimit,
                filterConfiguration: apiFilterConfiguration,
                sortConfiguration: apiSortConfiguration,
                searchConfiguration: args.searchConfiguration,
                intellsysBaseUrl: args.intellsysBaseUrl,
                accessToken: args.accessToken,
            };

            // `/journeys/events/get` only returns journeys that already have events (e.g. calls).
            // Load the full journey set from `/journeys/get`, then attach events when present.
            const [allJourneysResult, journeysWithEventsResult] = await Promise.all([getEventContainers(fetchArgs), getEventContainersWithEvents(fetchArgs)]);
            if (allJourneysResult.success !== true) {
                throw allJourneysResult.error;
            }

            const eventsByJourneyId = new Map<Uuid, EventContainerWithEvents["events"]>();
            if (journeysWithEventsResult.success === true) {
                for (const journey of journeysWithEventsResult.data) {
                    eventsByJourneyId.set(journey.id, journey.events);
                }
            }

            const journeysWithEvents: Array<EventContainerWithEvents> = allJourneysResult.data.map((journey) => ({
                ...journey,
                events: eventsByJourneyId.get(journey.id) ?? [],
            }));

            const searchText = args.searchConfiguration.searchValue?.trim() ?? "";
            return generateAmaronDispositionKanbanData({
                journeys: journeysWithEvents.map(args.journeySyntheticFn),
                filterConfiguration: args.filterConfiguration,
                searchConfiguration: args.searchConfiguration,
                viewLevel: args.dispositionLevel,
                journeysDisplayNameOttopilotStringTemplate: args.journeysDisplayNameOttopilotStringTemplate,
                apiSearchApplied: searchText !== "",
                eventContainerTemplate: args.eventContainerTemplate,
            });
        },
        staleTime: Infinity,
    });
}

export function invalidateAmaronDispositionKanbanDatasetQueries(queryClient: QueryClient, workspaceId: Uuid): Promise<void> {
    return Promise.all([
        queryClient.invalidateQueries({
            queryKey: [AMARON_DISPOSITION_KANBAN_DATASET_QUERY_KEY_ROOT, workspaceId],
        }),
        invalidateAmaronDispositionTrackerQueries(queryClient, workspaceId),
    ]).then(() => undefined);
}

export function invalidateAmaronDispositionTrackerQueries(queryClient: QueryClient, workspaceId: Uuid): Promise<void> {
    return queryClient.invalidateQueries({
        queryKey: [AMARON_DISPOSITION_TRACKER_QUERY_KEY_ROOT, workspaceId],
    });
}

function buildOttopilotDataViewerQueryKeyPrefix(queryKeyPrefix: ReadonlyArray<string | null | undefined> | undefined, workspaceId: Uuid): Array<string> {
    const prefix = (queryKeyPrefix ?? []).filter((segment): segment is string => segment !== null && segment !== undefined);
    if (prefix.length > 0) {
        return prefix;
    }
    return [AMARON_DISPOSITION_TRACKER_DATA_VIEWER_QUERY_KEY_ROOT, workspaceId];
}

export function refreshAmaronDispositionTrackerData(
    queryClient: QueryClient,
    workspaceId: Uuid,
    queryKeyPrefix?: ReadonlyArray<string | null | undefined>,
): Promise<void> {
    const prefix = buildOttopilotDataViewerQueryKeyPrefix(queryKeyPrefix, workspaceId);
    const ottopilotKey = ["ottopilotDataViewer", ...prefix];
    return Promise.all([
        invalidateAmaronDispositionKanbanDatasetQueries(queryClient, workspaceId),
        queryClient.invalidateQueries({queryKey: ottopilotKey}),
        invalidateOttopilotDataViewerQueries(queryClient, prefix),
    ]).then(() => undefined);
}

export function optimisticallyApplyAmaronDispositionTrackerJourneyUpdate(args: {
    queryClient: QueryClient;
    queryKeyPrefix: ReadonlyArray<string | null>;
    journeyId: Uuid;
    newDispositionId: Uuid;
    viewLevel: AmaronDispositionKanbanViewLevel;
    kanbanColumns: ReadonlyArray<{id: string; slug: string}>;
    eventContainerTemplate: EventContainerTemplate;
}): void {
    const queryKeyPrefix = args.queryKeyPrefix.filter(
        (segment): segment is string => segment !== null,
    );
    const ottopilotPrefix = ["ottopilotDataViewer", ...queryKeyPrefix];
    const slugToColumnId = new Map(args.kanbanColumns.map((column) => [column.slug, column.id]));
    const columnIdToSlug = new Map(args.kanbanColumns.map((column) => [column.id, column.slug]));

    let mappedJourney: AmaronDispositionKanbanJourney | null = null;
    let oldSlug: string | null = null;
    let newSlug: string | null = null;

    const buildMappedJourney = (existingRow: AmaronDispositionKanbanJourney): AmaronDispositionKanbanJourney | null => {
        const updatedJourney: EventContainerWithEvents = {
            ...existingRow,
            additionalColumnValues: {
                ...existingRow.additionalColumnValues,
                [getEventContainerDispositionColumnId(args.eventContainerTemplate)]: args.newDispositionId,
            },
            updatedAt: new Date().toISOString() as Iso8601DateTime,
        };
        const synthetic = amaronJourneySyntheticFunction(updatedJourney, args.newDispositionId);
        return toAmaronDispositionKanbanJourney(synthetic, args.viewLevel, args.eventContainerTemplate);
    };

    for (const [, data] of args.queryClient.getQueriesData<unknown>({
        queryKey: ottopilotPrefix,
    })) {
        if (mappedJourney !== null) {
            break;
        }
        const infinite = data as InfiniteData<Array<AmaronDispositionKanbanJourney>> | undefined;
        if (infinite === undefined || Array.isArray(infinite.pages) !== true) {
            continue;
        }
        for (const page of infinite.pages) {
            const existingRow = page.find((row) => row.id === args.journeyId);
            if (existingRow === undefined) {
                continue;
            }
            oldSlug = existingRow[AMARON_KANBAN_COLUMN_FIELD];
            mappedJourney = buildMappedJourney(existingRow);
            if (mappedJourney !== null) {
                newSlug = mappedJourney[AMARON_KANBAN_COLUMN_FIELD];
                break;
            }
        }
    }

    if (mappedJourney === null || newSlug === null) {
        return;
    }

    for (const [queryKey, data] of args.queryClient.getQueriesData<unknown>({
        queryKey: ottopilotPrefix,
    })) {
        if (Array.isArray(queryKey) !== true) {
            continue;
        }
        const kanbanRowsIndex = queryKey.indexOf("kanban");
        if (kanbanRowsIndex < 0 || queryKey[kanbanRowsIndex - 1] !== "rows") {
            continue;
        }
        const columnId = String(queryKey[kanbanRowsIndex + 1] ?? "");
        const columnSlug = columnIdToSlug.get(columnId);
        const infinite = data as InfiniteData<Array<AmaronDispositionKanbanJourney>> | undefined;
        if (infinite === undefined || Array.isArray(infinite.pages) !== true) {
            continue;
        }
        const withoutJourney = infinite.pages.map((page) => page.filter((row) => row.id !== args.journeyId));
        if (columnSlug === newSlug) {
            const firstPage = withoutJourney[0] ?? [];
            withoutJourney[0] = [mappedJourney, ...firstPage.filter((row) => row.id !== args.journeyId)];
        }
        args.queryClient.setQueryData(queryKey, {
            ...infinite,
            pages: withoutJourney,
        });
    }

    if (oldSlug === null || oldSlug === newSlug) {
        return;
    }

    const oldColumnId = slugToColumnId.get(oldSlug);
    const newColumnId = slugToColumnId.get(newSlug);
    for (const [queryKey, data] of args.queryClient.getQueriesData<unknown>({
        queryKey: ottopilotPrefix,
    })) {
        if (Array.isArray(queryKey) !== true) {
            continue;
        }
        if (queryKey.includes("kanbanColumnRowCount") === true) {
            if (typeof data !== "number") {
                continue;
            }
            const countColumnId = String(queryKey[queryKey.indexOf("kanbanColumnRowCount") + 1] ?? "");
            let count = data;
            if (countColumnId === oldColumnId) {
                count = Math.max(0, count - 1);
            }
            if (countColumnId === newColumnId) {
                count += 1;
            }
            if (count !== data) {
                args.queryClient.setQueryData(queryKey, count);
            }
            continue;
        }
        if (queryKey.includes("kanbanColumnRowCounts") !== true && queryKey.includes("kanbanColumnCounts") !== true) {
            continue;
        }
        if (data === null || data === undefined || typeof data !== "object" || Array.isArray(data) === true) {
            continue;
        }
        const counts = {...(data as Record<string, number>)};
        if (oldColumnId !== undefined) {
            counts[oldColumnId] = Math.max(0, (counts[oldColumnId] ?? 0) - 1);
        }
        if (newColumnId !== undefined) {
            counts[newColumnId] = (counts[newColumnId] ?? 0) + 1;
        }
        args.queryClient.setQueryData(queryKey, counts);
    }
}

export function applyAmaronDispositionTrackerJourneyDispositionUpdate(args: {
    queryClient: QueryClient;
    workspaceId: Uuid;
    queryKeyPrefix: ReadonlyArray<string | null>;
    journeyId: Uuid;
    newDispositionId: Uuid;
    viewLevel: AmaronDispositionKanbanViewLevel;
    kanbanColumns: ReadonlyArray<{id: string; slug: string}>;
    eventContainerTemplate: EventContainerTemplate;
}): void {
    optimisticallyApplyAmaronDispositionTrackerJourneyUpdate(args);
}
